import React from 'react'

import { ZariContext } from '_zari/context'

import { Portlet, Table } from '_zari/core'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import { EstadosActivoFijoConstant } from 'constants/index'

import * as ActivosFijosService from 'services/activosFijos.service'

import * as Modals from './modals'
import { DownloadAxiosFile, ZrSwal } from 'utils'

const portletBreadcrumbs = [{ name: 'Activos Fijos' }]

const tableHeaderRows = [
  {
    label: 'CODIGO',
    filter: {
      type: 'text',
      name: 'codigo'
    }
  },
  {
    label: 'DESCRIPCION',
    filter: {
      type: 'text',
      name: 'descripcion'
    }
  },
  { label: 'OFICINA' },
  { label: 'FEC ADQUISICION' },
  { label: 'FEC ENTREGA' },
  { label: 'VALOR ADQ.' },
  { label: 'ESTADO' },
  { label: '', width: 60 }
]

const ListaPage = ({ history }) => {
  const __zariContext = React.useContext(ZariContext)

  const tablaRef = React.useRef(null)
  const idActivoRef = React.useRef(null)
  const barcodeInfoRef = React.useRef({ codigo: '', descripcion: '' })

  const [mostrarModal, setMostrarModal] = React.useState(false)
  const [mostrarImportarActivosFijosModal, setMostrarImportarActivosFijosModal] = React.useState(false)
  const [mostrarCodigoBarrasModal, setMostrarCodigoBarrasModal] = React.useState(false)
  const [itemsLote, setItemsLote] = React.useState(null)

  const clickNuevo = React.useCallback(() => {
    history.push(`/${CodigosTransaccionesConstant.ACTIVOSFIJOS_NUEVO}`)
  }, [history])

  const clickEditar = React.useCallback(
    ({ _id }) => {
      history.push(`/${CodigosTransaccionesConstant.ACTIVOSFIJOS_EDITAR}/${_id}`)
    },
    [history]
  )

  const clickLibroActivos = React.useCallback(({ _id }) => {
    idActivoRef.current = _id
    setMostrarModal(true)
  }, [])

  const clickCodigoBarras = React.useCallback(({ codigo, descripcion }) => {
    barcodeInfoRef.current = { codigo, descripcion }
    setItemsLote(null)
    setMostrarCodigoBarrasModal(true)
  }, [])

  const clickImprimirLote = React.useCallback(async () => {
    try {
      const { data } = await ActivosFijosService.getSelect({ cuenta: '' })
      const items = (data || [])
        .filter(x => x.codigo)
        .map(x => ({ codigo: x.codigo, descripcion: x.nombre || x.descripcion || '' }))
      if (items.length === 0) {
        __zariContext.snack('error', 'No hay activos fijos para imprimir')
        return
      }
      setItemsLote(items)
      setMostrarCodigoBarrasModal(true)
    } catch (e) {
      __zariContext.snack('error', e?.data?.error || 'Error al cargar activos')
    }
  }, [__zariContext])

  const clickEliminar = React.useCallback(
    ({ _id }) => {
      ZrSwal.showAlert({
        mensaje: '¿Seguro que desea eliminar el activo fijo?',
        tipo: 'error',
        onSuccess: () => {
          ActivosFijosService.eliminar(_id)
            .then(() => {
              __zariContext.snack('success', 'Registro eliminado!')
              tablaRef.current.reload()
            })
            .catch(({ data: { error } }) => __zariContext.snack('error', error))
        }
      })
    },
    [__zariContext]
  )

  const clickImportar = React.useCallback(() => {
    setMostrarImportarActivosFijosModal(true)
  }, [])

  const clickDescargarPlantilla = React.useCallback(() => {
    ActivosFijosService.descargarPlantilla()
      .then(({ data, headers }) => {
        __zariContext.snack('success', 'Plantilla Descargada')
        DownloadAxiosFile.downloadAsBlob(data, headers, 'P-ImportarActivosFijos.xlsx')
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext])

  return (
    <>
      <Portlet>
        <Portlet.Header
          title='Activos Fijos'
          breadcrumbs={portletBreadcrumbs}
          buttons={[
            { name: 'Nuevo', onClick: clickNuevo },
            { name: 'Importar', onClick: clickImportar },
            { name: 'Descargar Plantilla', onClick: clickDescargarPlantilla },
            { name: 'Imprimir Códigos (Lote)', onClick: clickImprimirLote }
          ]}
        />
        <Portlet.Body>
          <Table.ServerSide
            useNest={true}
            ref={tablaRef}
            useFilters={true}
            url='/activos-fijos'
            headRows={tableHeaderRows}
            rowTemplate={row => (
              <Table.Row key={row._id}>
                <Table.Cell>{row.codigo || '--'}</Table.Cell>
                <Table.Cell>{row.descripcion || '--'}</Table.Cell>
                <Table.Cell>{row.oficina || '--'}</Table.Cell>
                <Table.Cell>{row.fechaAdquisicion || '--'}</Table.Cell>
                <Table.Cell>{row.fechaEntrega || '--'}</Table.Cell>
                <Table.Cell>{row.valorAdquisicion != null ? Number(row.valorAdquisicion).toFixed(2) : '--'}</Table.Cell>
                <Table.Cell>
                  {row.estado && EstadosActivoFijoConstant.__dictionary[row.estado]
                    ? EstadosActivoFijoConstant.__dictionary[row.estado].nombre
                    : '--'}
                </Table.Cell>
                <Table.Cell>
                  <Table.Dropdown>
                    {({ handleClose }) => [
                      <Table.Dropdown.Item key='libro-activos' handleClose={handleClose} onClick={e => clickLibroActivos({ _id: row._id })}>
                        Libro Activos
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item
                        key='codigo-barras'
                        handleClose={handleClose}
                        onClick={e => clickCodigoBarras({ codigo: row.codigo, descripcion: row.descripcion })}
                      >
                        Código de Barras
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item key='editar' handleClose={handleClose} onClick={e => clickEditar({ _id: row._id })}>
                        Editar
                      </Table.Dropdown.Item>,
                      <Table.Dropdown.Item key='eliminar' handleClose={handleClose} onClick={e => clickEliminar({ _id: row._id })}>
                        Eliminar
                      </Table.Dropdown.Item>
                    ]}
                  </Table.Dropdown>
                </Table.Cell>
              </Table.Row>
            )}
          />
        </Portlet.Body>
      </Portlet>
      {mostrarModal && <Modals.LibroActivosModal handleClose={() => setMostrarModal(false)} show={mostrarModal} idActivo={idActivoRef.current} />}
      {mostrarCodigoBarrasModal && (
        <Modals.CodigoBarrasModal
          handleClose={() => {
            setMostrarCodigoBarrasModal(false)
            setItemsLote(null)
          }}
          show={mostrarCodigoBarrasModal}
          codigo={barcodeInfoRef.current.codigo}
          descripcion={barcodeInfoRef.current.descripcion}
          items={itemsLote}
        />
      )}
      {mostrarImportarActivosFijosModal && (
        <Modals.ImportarActivosFijosModal
          handleClose={() => setMostrarImportarActivosFijosModal(false)}
          show={mostrarImportarActivosFijosModal}
          reload={tablaRef.current.reload}
        />
      )}
    </>
  )
}

export default React.memo(ListaPage)
